import { Node, mergeAttributes } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Suggestion } from '@tiptap/suggestion';

import { ProfileNodeView } from '../../components/extensions/ProfileNodeView';

/**
 * The plugin key for the profile plugin.
 * @default 'profile'
 */
export const ProfilePluginKey = new PluginKey('profile');

const Profile = Node.create({
  name: 'profile',

  priority: 101,

  addOptions() {
    return {
      HTMLAttributes: {},
      profiles: [],
      deleteTriggerWithBackspace: true,
      suggestion: {
        char: '@',
        pluginKey: ProfilePluginKey,
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-target'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }

          return {
            'data-target': attributes.id,
          };
        },
      },
      dataId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.dataId) {
            return {};
          }

          return {
            'data-id': attributes.dataId,
          };
        },
      },
    };
  },

  // Need this for the sake of the Edit mode.
  // This is how we get the ID out, which we can then use to get the profile HTML from the profiles array.
  parseHTML() {
    return [
      {
        tag: `entity[data-type="${this.name}"]`,
        getAttrs: (element) => {
          const id = element.getAttribute('data-target');
          const dataId = element.getAttribute('data-id');
          return {
            id,
            dataId: dataId || null,
          };
        },
      },
      {
        tag: `span[data-type="${this.name}"]`,
        getAttrs: (element) => {
          const id = element.getAttribute('data-target');
          const dataId = element.getAttribute('data-id');
          return {
            id,
            dataId: dataId || null,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = mergeAttributes(
      {
        'data-type': this.name,
        'data-target': node.attrs.id,
        'data-id': node.attrs.dataId,
      },
      this.options.HTMLAttributes,
      HTMLAttributes,
    );

    return ['entity', attrs];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ProfileNodeView);
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isProfile = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isProfile = true;
              tr.insertText(this.options.deleteTriggerWithBackspace ? '' : this.options.suggestion.char || '', pos, pos + node.nodeSize);

              return false;
            }
          });

          return isProfile;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default Profile;
